import { getAdminTermsDetail, queryKeys, updateAdminTerms } from "@dutyon/apis";
import {
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Paper,
  RichEditor,
  SafeHtml,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export function TermsEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [categoryName, setCategoryName] = useState<string>("");
  const [version, setVersion] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [isRequired, setIsRequired] = useState<boolean>(true);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isHtmlMode, setIsHtmlMode] = useState<boolean>(false);
  const [releasedAt, setReleasedAt] = useState<Date | null>(null);

  // 약관 상세조회
  const { data: termsDetail, isLoading } = useQuery({
    queryKey: queryKeys.terms.detail(id || ""),
    queryFn: () => getAdminTermsDetail(id || ""),
    enabled: !!id,
  });

  useEffect(() => {
    if (termsDetail) {
      setCategoryName(termsDetail.category?.name || termsDetail.categoryKey);
      setVersion(termsDetail.version);
      setTitle(termsDetail.title);
      setContent(termsDetail.content);
      setIsRequired(termsDetail.isRequired);
      setIsActive(termsDetail.isActive);
      setReleasedAt(termsDetail.releasedAt);
    }
  }, [termsDetail]);

  const updateMutation = useMutation({
    mutationFn: updateAdminTerms,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.terms.all });
      navigate("/terms");
    },
  });

  const handleSubmit = () => {
    if (!title.trim()) {
      alert("약관 제목을 입력해주세요.");
      return;
    }
    if (!content.trim()) {
      alert("약관 내용을 입력해주세요.");
      return;
    }

    const isReleased = releasedAt !== null;

    if (isActive && !termsDetail?.isActive) {
      const confirmMsg =
        "이 약관을 즉시 활성화(발행)하시겠습니까?\n발행 즉시 동일한 약관 구분의 기존 활성 약관이 비활성화되며, 모든 사용자는 다음 로그인 시 재동의가 요구됩니다.";
      if (!confirm(confirmMsg)) {
        return;
      }
    }

    const payload: Parameters<typeof updateAdminTerms>[0]["data"] = { isActive };
    // 발행 전 임시저장(Draft) 약관인 경우만 본문 수정 가능
    if (!isReleased) {
      payload.title = title;
      payload.content = content;
      payload.isRequired = isRequired;
    }

    if (id) {
      updateMutation.mutate({ id, data: payload });
    }
  };

  const isReleased = releasedAt !== null;

  if (isLoading) {
    return (
      <TitleContainer title="약관 수정">
        <Card withBorder radius="lg" p="xl">
          <Text ta="center">약관 상세 정보를 불러오는 중...</Text>
        </Card>
      </TitleContainer>
    );
  }

  return (
    <TitleContainer
      title="약관 수정 및 설정"
      rightSection={
        <Button
          variant="subtle"
          color="gray"
          leftSection={<ArrowLeft size={16} />}
          onClick={() => navigate("/terms")}
        >
          목록으로 돌아가기
        </Button>
      }
    >
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="md">
            {isReleased && (
              <Paper
                bg="yellow.0"
                p="md"
                radius="md"
                withBorder
                style={{ borderColor: "var(--mantine-color-yellow-3)" }}
              >
                <Group gap={8} align="flex-start" wrap="nowrap">
                  <AlertTriangle
                    size={18}
                    color="var(--mantine-color-yellow-7)"
                    style={{ marginTop: 2 }}
                  />
                  <Text size="xs" c="yellow.9" fw={500}>
                    이미 발행(Release)되어 적용된 기록이 있는 약관입니다. 버전 및 내용 수정은
                    제한되며, <strong>활성화(발행중) 여부</strong> 상태만 조율할 수 있습니다. 내용을
                    변경하려면 목록에서 새로운 버전의 약관을 발행해 주세요.
                  </Text>
                </Group>
              </Paper>
            )}

            <Card withBorder radius="lg" shadow="sm" p="lg">
              <Stack gap="md">
                <Text fw={700} size="lg">
                  약관 세부사항
                </Text>

                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput label="약관 구분 (Category)" value={categoryName} disabled />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput label="약관 버전 (Version)" value={version} disabled />
                  </Grid.Col>
                </Grid>

                <TextInput
                  label="약관 제목"
                  placeholder="사용자에게 보여질 약관 제목을 입력하세요."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isReleased}
                  required
                />

                <Group justify="space-between" mt="xs">
                  <Group gap="lg">
                    <Switch
                      label="필수 동의 여부"
                      checked={isRequired}
                      onChange={(e) => setIsRequired(e.currentTarget.checked)}
                      disabled={isReleased}
                    />
                    <Switch
                      label="즉시 활성화 (발행)"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.currentTarget.checked)}
                    />
                  </Group>
                  <Switch
                    label="HTML 직접 타이핑 모드"
                    checked={isHtmlMode}
                    onChange={(e) => setIsHtmlMode(e.currentTarget.checked)}
                    disabled={isReleased}
                  />
                </Group>

                <Divider label="약관 내용" labelPosition="center" my="sm" />

                <Stack gap="xs">
                  {isHtmlMode ? (
                    <Textarea
                      label="HTML 소스 직접 편집"
                      placeholder="HTML 코드를 직접 작성하세요."
                      minRows={14}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      disabled={isReleased}
                      styles={{ input: { fontFamily: "monospace", fontSize: "13px" } }}
                    />
                  ) : (
                    <RichEditor
                      label="WYSIWYG 에디터 편집"
                      value={content}
                      onChange={(html) => setContent(html)}
                      placeholder="약관 전문 내용을 작성하세요. HTML 태그가 지원됩니다."
                      minHeight={300}
                      readOnly={isReleased}
                    />
                  )}
                </Stack>

                <Group justify="flex-end" mt="lg">
                  <Button variant="light" color="gray" onClick={() => navigate("/terms")}>
                    취소
                  </Button>
                  <Button
                    leftSection={<Save size={16} />}
                    onClick={handleSubmit}
                    loading={updateMutation.isPending}
                  >
                    저장
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="md" style={{ position: "sticky", top: 20 }}>
            <Card withBorder radius="lg" shadow="sm" p="lg" bg="gray.0">
              <Stack gap="xs">
                <Text fw={700} size="md">
                  실시간 렌더링 프리뷰
                </Text>
                <Text size="xs" c="dimmed">
                  사용자 화면에 노출될 최종 HTML 모습입니다.
                </Text>
                <Divider my="xs" />
                <Paper
                  withBorder
                  p="md"
                  radius="md"
                  bg="white"
                  style={{
                    minHeight: 450,
                    maxHeight: 600,
                    overflowY: "auto",
                    fontSize: "13px",
                    lineHeight: "1.6",
                  }}
                >
                  {title && (
                    <Text fw={700} size="sm" mb="sm" c="blue.7">
                      [{isRequired ? "필수" : "선택"}] {title}
                    </Text>
                  )}
                  {content ? (
                    <SafeHtml html={content} />
                  ) : (
                    <Text size="xs" c="dimmed" ta="center" mt="xl">
                      작성 중인 내용이 여기에 실시간으로 렌더링됩니다.
                    </Text>
                  )}
                </Paper>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>
    </TitleContainer>
  );
}
